import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";

import StaticSeatMap from "public/images/inlandempire66ers_flex/66ers2025StadiumMap.jpg";
import FindGamesBanner from "public/images/inlandempire66ers_flex/FamilyPack-1486x148.jpg";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "inlandempire66ers_flex",
  browserTitle: "Inland Empire Family Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "Inland Empire 66ers",

  // Seat map configs
  seatMapConfigs: {
    venueId: "", // Not available
    mapId: "", // Not available
    staticSeatMapImage: StaticSeatMap.src, // Use empty string if 'UploadedToTicket'
    customSeatMapStaticImageStyle: {
      width: "70%",
      marginX: "auto",
      display: "block",
    },
    isView3dButtonHidden: true,
    isShowAvailableSeatsButtonHidden: true,
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats! You've unlocked 10% off your tickets! Add 1 more game for 20% off, 2 more games for 30% off & the chance to watch fireworks on the field, 3+ games for 40% off & an exclusive giveaway opportunity on Sept 6th, or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets! Add 1 more game for 30% off & the chance to watch fireworks on the field, 2+ games for 40% off & an exclusive giveaway opportunity on Sept 6th, or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        discount: 30,
        customAditionalMessage:
          " for 30% off & the chance to watch fireworks on the field",
        customProgressBarMessage:
          "Congrats! You've unlocked 30% off your tickets & the chance to watch fireworks on the field! Add 1 more game for 40% off & an exclusive giveaway opportunity on Sept 6th or checkout now.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 3,
        discount: 40,
        customAditionalMessage:
          " for 40% off & an exclusive giveaway opportunity on Sept 6th?",
        customProgressBarMessage:
          "Congrats! You've unlocked 40% off your tickets & an exclusive giveaway opportunity on Sept 6th! Add more games or checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 20,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
    isPerksEnabled: true,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom,
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=ISSM_MYTIXX&orgid=57610#/auth/login",

  // Images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: "",
    checkoutStatusPageImage: "",
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Inland-empire-66ers.png?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Inland-empire-66ers.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    checkoutStatusPage: {
      backgroundColor: "#f47836",
    },
    progressBar: {
      customProgressBarSize: 600,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:9098889922"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (909) 888-9922
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#010101",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Inland Empire 66ers tickets are confirmed! You will be notified when your tickets are available to be managed.",
    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
